import { useState } from 'react'

interface AuditEntry {
  id: string
  timestamp: string
  action: string
  actor: {
    name: string
    email: string
  }
  target: string
  details: string
  category: 'evaluation' | 'decision' | 'settings' | 'access'
}

interface ComplianceMetric {
  label: string
  value: string
  status: 'good' | 'warning' | 'critical'
  description: string
}

// Mock data - in production this comes from API
const MOCK_AUDIT_LOG: AuditEntry[] = [
  {
    id: '1',
    timestamp: '2026-01-04 14:32:18',
    action: 'Evaluation submitted',
    actor: { name: 'Sarah Kim', email: 'sarah@acme.com' },
    target: 'Alex Chen (Backend Engineer)',
    details: 'Technical interview evaluation: Strong Hire',
    category: 'evaluation',
  },
  {
    id: '2',
    timestamp: '2026-01-04 13:15:42',
    action: 'Decision finalized',
    actor: { name: 'Mike Thompson', email: 'mike@acme.com' },
    target: 'Jordan Lee (Product Designer)',
    details: 'Moved to offer stage with approval',
    category: 'decision',
  },
  {
    id: '3',
    timestamp: '2026-01-04 11:28:05',
    action: 'Rubric modified',
    actor: { name: 'Emily Chen', email: 'emily@acme.com' },
    target: 'System Design rubric',
    details: 'Updated weight for "High-Level Design" dimension from 25% to 30%',
    category: 'settings',
  },
  {
    id: '4',
    timestamp: '2026-01-04 10:45:33',
    action: 'Evaluator added',
    actor: { name: 'David Park', email: 'david@acme.com' },
    target: 'Chris Martinez (Engineering Manager)',
    details: 'Added as technical evaluator',
    category: 'access',
  },
  {
    id: '5',
    timestamp: '2026-01-03 16:22:11',
    action: 'Evaluation submitted',
    actor: { name: 'Lisa Wang', email: 'lisa@acme.com' },
    target: 'Morgan Smith (Staff Engineer)',
    details: 'Behavioral interview evaluation: Hire',
    category: 'evaluation',
  },
  {
    id: '6',
    timestamp: '2026-01-03 14:18:29',
    action: 'Role created',
    actor: { name: 'Emily Chen', email: 'emily@acme.com' },
    target: 'Senior Product Manager',
    details: 'New role added to Product job family',
    category: 'settings',
  },
  {
    id: '7',
    timestamp: '2026-01-03 11:05:44',
    action: 'Decision conflict flagged',
    actor: { name: 'System', email: 'system@jumpstone.hr' },
    target: 'Taylor Wilson (Backend Engineer)',
    details: 'Signal conflict detected: Evaluator scores diverge by >2 points',
    category: 'decision',
  },
]

const MOCK_METRICS: ComplianceMetric[] = [
  {
    label: 'Evaluation completion rate',
    value: '94%',
    status: 'good',
    description: 'Percentage of scheduled evaluations completed on time',
  },
  {
    label: 'Average time to decision',
    value: '6.2 days',
    status: 'good',
    description: 'From first evaluation to final decision',
  },
  {
    label: 'Evaluator calibration score',
    value: '0.87',
    status: 'good',
    description: 'Cross-evaluator consistency (1.0 = perfect alignment)',
  },
  {
    label: 'Structured feedback rate',
    value: '78%',
    status: 'warning',
    description: 'Evaluations with detailed rubric-based feedback',
  },
]

function DownloadIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
    </svg>
  )
}

function FunnelIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 01-.659 1.591l-5.432 5.432a2.25 2.25 0 00-.659 1.591v2.927a2.25 2.25 0 01-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 00-.659-1.591L3.659 7.409A2.25 2.25 0 013 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0112 3z" />
    </svg>
  )
}

function MagnifyingGlassIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
    </svg>
  )
}

function CategoryBadge({ category }: { category: AuditEntry['category'] }) {
  const config = {
    evaluation: { bg: 'bg-blue-50', text: 'text-blue-700', label: 'Evaluation' },
    decision: { bg: 'bg-emerald-50', text: 'text-emerald-700', label: 'Decision' },
    settings: { bg: 'bg-amber-50', text: 'text-amber-700', label: 'Settings' },
    access: { bg: 'bg-purple-50', text: 'text-purple-700', label: 'Access' },
  }
  
  const { bg, text, label } = config[category]
  
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-md text-xs font-medium ${bg} ${text}`}>
      {label}
    </span>
  )
}

function MetricCard({ metric }: { metric: ComplianceMetric }) {
  const statusColors = {
    good: 'text-emerald-600',
    warning: 'text-amber-600',
    critical: 'text-red-600',
  }

  return (
    <div className="p-4 bg-surface-50 rounded-xl">
      <p className="text-sm text-surface-500">{metric.label}</p>
      <p className={`text-2xl font-semibold mt-1 ${statusColors[metric.status]}`}>
        {metric.value}
      </p>
      <p className="text-xs text-surface-400 mt-1">{metric.description}</p>
    </div>
  )
}

export default function AuditComplianceSettings() {
  const [auditLog] = useState(MOCK_AUDIT_LOG)
  const [metrics] = useState(MOCK_METRICS)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string>('all')

  const filteredLog = auditLog.filter(entry => {
    const matchesSearch = searchQuery === '' || 
      entry.action.toLowerCase().includes(searchQuery.toLowerCase()) ||
      entry.actor.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      entry.target.toLowerCase().includes(searchQuery.toLowerCase()) ||
      entry.details.toLowerCase().includes(searchQuery.toLowerCase())
    
    const matchesCategory = selectedCategory === 'all' || entry.category === selectedCategory
    
    return matchesSearch && matchesCategory
  })

  return (
    <div className="divide-y divide-surface-100">
      {/* Header */}
      <div className="p-6 sm:p-8">
        <h2 className="text-lg font-semibold text-surface-900">Audit & Compliance</h2>
        <p className="mt-1 text-surface-600">
          Track every action taken during the hiring process. Complete attribution and accountability.
        </p>
      </div>

      {/* Compliance metrics */}
      <div className="p-6 sm:p-8">
        <h3 className="text-sm font-medium text-surface-900 mb-4">Hiring process health</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {metrics.map((metric, idx) => (
            <MetricCard key={idx} metric={metric} />
          ))}
        </div>
      </div>

      {/* Data retention info */}
      <div className="p-6 sm:p-8 bg-surface-50">
        <div className="flex gap-4">
          <div className="shrink-0 w-10 h-10 rounded-xl bg-brand-100 flex items-center justify-center">
            <svg className="w-5 h-5 text-brand-700" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
            </svg>
          </div>
          <div>
            <h3 className="font-medium text-brand-900">Data retention & compliance</h3>
            <p className="mt-1 text-sm text-brand-700">
              All evaluation data and audit logs are retained for 7 years in accordance with EEOC guidelines. 
              Data is encrypted at rest and in transit. You can request a full data export at any time.
            </p>
          </div>
        </div>
      </div>

      {/* Audit log */}
      <div className="p-6 sm:p-8">
        <div className="flex items-center justify-between gap-4 mb-6">
          <h3 className="text-sm font-medium text-surface-900">Activity log</h3>
          <button className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border border-surface-300 text-surface-700 hover:bg-surface-50 transition-colors">
            <DownloadIcon className="w-4 h-4" />
            Export log
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-400" />
            <input
              type="text"
              placeholder="Search actions, people, or details..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-surface-300 text-surface-900 placeholder:text-surface-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
            />
          </div>
          <div className="relative">
            <FunnelIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-400 pointer-events-none" />
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="appearance-none pl-10 pr-10 py-2.5 rounded-lg border border-surface-300 text-surface-900 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 cursor-pointer"
            >
              <option value="all">All categories</option>
              <option value="evaluation">Evaluations</option>
              <option value="decision">Decisions</option>
              <option value="settings">Settings</option>
              <option value="access">Access</option>
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
              <svg className="w-4 h-4 text-surface-400" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 15L12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9" />
              </svg>
            </div>
          </div>
        </div>

        {/* Log entries */}
        <div className="border border-surface-200 rounded-xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-surface-50 border-b border-surface-200">
                <th className="text-left text-xs font-medium text-surface-500 uppercase tracking-wide px-4 py-3">
                  Timestamp
                </th>
                <th className="text-left text-xs font-medium text-surface-500 uppercase tracking-wide px-4 py-3">
                  Action
                </th>
                <th className="text-left text-xs font-medium text-surface-500 uppercase tracking-wide px-4 py-3 hidden lg:table-cell">
                  Actor
                </th>
                <th className="text-left text-xs font-medium text-surface-500 uppercase tracking-wide px-4 py-3 hidden md:table-cell">
                  Category
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-100">
              {filteredLog.map((entry) => (
                <tr key={entry.id} className="hover:bg-surface-50 transition-colors">
                  <td className="px-4 py-3">
                    <p className="text-sm font-mono text-surface-600">{entry.timestamp}</p>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-sm font-medium text-surface-900">{entry.action}</p>
                    <p className="text-sm text-surface-500">{entry.target}</p>
                    <p className="text-xs text-surface-400 mt-1">{entry.details}</p>
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell">
                    <p className="text-sm text-surface-900">{entry.actor.name}</p>
                    <p className="text-xs text-surface-500">{entry.actor.email}</p>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <CategoryBadge category={entry.category} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="mt-4 flex items-center justify-between text-sm text-surface-500">
          <p>Showing {filteredLog.length} of {auditLog.length} entries</p>
          <div className="flex items-center gap-2">
            <button className="px-3 py-1.5 rounded border border-surface-300 hover:bg-surface-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" disabled>
              Previous
            </button>
            <button className="px-3 py-1.5 rounded border border-surface-300 hover:bg-surface-50 transition-colors">
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Empty state for filtered results */}
      {filteredLog.length === 0 && (
        <div className="p-12 text-center">
          <div className="w-12 h-12 rounded-full bg-surface-100 flex items-center justify-center mx-auto mb-4">
            <MagnifyingGlassIcon className="w-6 h-6 text-surface-400" />
          </div>
          <h3 className="font-medium text-surface-900 mb-1">No matching entries</h3>
          <p className="text-surface-500 text-sm">
            Try adjusting your search or filter criteria.
          </p>
        </div>
      )}
    </div>
  )
}

